import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { organizationsApi } from '../api/organizations';
import DashboardLayout from '../components/DashboardLayout';
import './DashboardPages.css';
import ImageUploadModal from '../components/ImageUploadModal';
import { s3Api } from '../api/s3';
import AppPreview from '../components/AppPreview';
import OrgQRCode from '../components/OrgQRCode';

function DashboardOrganization() {
	const { orgData, setOrgData } = useAuth();
	const [error, setError] = useState('');
	const [showSuccess, setShowSuccess] = useState(false);
	const [formData, setFormData] = useState({
		name: '',
		addressOne: '',
		addressTwo: '',
		city: '',
		state: '',
		zipCode: '',
		phoneNumber: '',
		website: '',
		facebook: '',
		x: '',
		instagram: '',
		primaryColor: '#3388B3',
		secondaryColor: '#666563',
		accentColor: '#9D94E8',
		orgPicture: null,
		coverImage: null,
		theme: 'default',
	});
	const [isImageModalOpen, setIsImageModalOpen] = useState(false);
	const [activeImageEdit, setActiveImageEdit] = useState(null);
	const [qrCodeUrl, setQrCodeUrl] = useState('');

	// Store the actual files until form submission
	const [pendingUploads, setPendingUploads] = useState({
		orgPicture: null,
		coverImage: null,
	});

	useEffect(() => {
		if (orgData) {
			setFormData({
				name: orgData.name || '',
				addressOne: orgData.addressOne || '',
				addressTwo: orgData.addressTwo || '',
				city: orgData.city || '',
				state: orgData.state || '',
				zipCode: orgData.zipCode || '',
				phoneNumber: orgData.phoneNumber || '',
				website: orgData.website || '',
				facebook: orgData.facebook || '',
				x: orgData.x || '',
				instagram: orgData.instagram || '',
				primaryColor: orgData.primaryColor || '#3388B3',
				secondaryColor: orgData.secondaryColor || '#666563',
				accentColor: orgData.accentColor || '#9D94E8',
				orgPicture: orgData.orgPicture || null,
				coverImage: orgData.coverImage || null,
				theme: orgData.theme || 'default',
			});
		}
	}, [orgData]);

	const handleColorChange = (colorType, value) => {
		setFormData((prev) => ({
			...prev,
			[colorType]: value,
		}));
	};

	const handleImageSave = ({ file }) => {
		if (!file || !activeImageEdit) return;

		// Store the file in pendingUploads
		setPendingUploads((prev) => ({
			...prev,
			[activeImageEdit]: file,
		}));

		// Create a preview URL for the UI
		setFormData((prev) => ({
			...prev,
			[activeImageEdit]: URL.createObjectURL(file),
		}));

		setIsImageModalOpen(false);
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		try {
			setError('');
			setShowSuccess(false);

			const updatedFormData = { ...formData };

			// Handle organization profile picture upload
			if (pendingUploads.orgPicture) {
				const orgPictureUrl = await s3Api.upload(
					orgData.id,
					pendingUploads.orgPicture,
					`Images/profile.${pendingUploads.orgPicture.name
						.split('.')
						.pop()}`
				);
				updatedFormData.orgPicture = orgPictureUrl;
			}

			// Handle cover image upload
			if (pendingUploads.coverImage) {
				const coverImageUrl = await s3Api.upload(
					orgData.id,
					pendingUploads.coverImage,
					`Images/cover.${pendingUploads.coverImage.name
						.split('.')
						.pop()}`
				);
				updatedFormData.coverImage = coverImageUrl;
			}

			const updatedOrg = await organizationsApi.update(
				orgData.id,
				updatedFormData
			);

			// Create a complete merged object with all updated values
			const newOrgData = {
				...orgData,
				...updatedFormData,
				...updatedOrg,
			};

			setOrgData(newOrgData);
			setFormData((prev) => ({
				...prev,
				...updatedFormData,
			}));

			setPendingUploads({
				orgPicture: null,
				coverImage: null,
			});

			setShowSuccess(true);
			setTimeout(() => setShowSuccess(false), 3000);
		} catch (err) {
			setError('Failed to update organization settings: ' + err.message);
		}
	};

	const handleDownloadQR = () => {
		const link = document.createElement('a');
		link.href = qrCodeUrl;
		link.download = `${orgData.name}-qr-code.png`;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};

	if (!orgData) {
		return (
			<DashboardLayout>
				<div className='dashboard-page'>
					<div className='content-section'>
						<p>Loading organization data...</p>
					</div>
				</div>
			</DashboardLayout>
		);
	}

	return (
		<DashboardLayout>
			<div className='dashboard-page'>
				<h1>Organization Settings</h1>
				<div className='content-section'>
					{error && <div className='error-message'>{error}</div>}

					<div
						className='org-cover-container'
						onClick={() => {
							setActiveImageEdit('coverImage');
							setIsImageModalOpen(true);
						}}>
						{formData.coverImage ? (
							// Display S3 stored cover image
							<img
								src={formData.coverImage}
								alt='Organization Cover'
								className='org-cover-image'
								onError={(e) => {
									console.error(
										'Error loading cover image:',
										e
									);
								}}
							/>
						) : pendingUploads.coverImage ? (
							// Display local preview of newly selected image
							<img
								src={URL.createObjectURL(
									pendingUploads.coverImage
								)}
								alt='Organization Cover Preview'
								className='org-cover-image'
							/>
						) : (
							<div className='org-cover-placeholder'>
								<span className='material-icons'>image</span>
								<span>Click to add cover image</span>
							</div>
						)}

						<div
							className='org-avatar-container'
							onClick={(e) => {
								e.stopPropagation();
								setActiveImageEdit('orgPicture');
								setIsImageModalOpen(true);
							}}>
							{formData.orgPicture ? (
								// Display S3 stored profile picture
								<img
									src={formData.orgPicture}
									alt='Organization'
									className='org-avatar'
									onError={(e) => {
										console.error(
											'Error loading profile image:',
											e
										);
									}}
								/>
							) : pendingUploads.orgPicture ? (
								// Display local preview of newly selected image
								<img
									src={URL.createObjectURL(
										pendingUploads.orgPicture
									)}
									alt='Organization Preview'
									className='org-avatar'
								/>
							) : (
								<div className='org-avatar-placeholder'>
									<span className='material-icons'>
										business
									</span>
								</div>
							)}
						</div>
					</div>

					<ImageUploadModal
						isOpen={isImageModalOpen}
						onClose={() => setIsImageModalOpen(false)}
						onSave={handleImageSave}
						shouldCrop={false}
					/>

					<form
						className='org-form'
						onSubmit={handleSubmit}>
						<div className='form-group'>
							<label>Organization Name</label>
							<input
								type='text'
								placeholder='Enter organization name'
								className='form-input'
								value={formData.name}
								onChange={(e) =>
									setFormData({
										...formData,
										name: e.target.value,
									})
								}
							/>
						</div>

						<div className='form-group'>
							<label>Address Line 1</label>
							<input
								type='text'
								placeholder='Street address'
								className='form-input'
								value={formData.addressOne}
								onChange={(e) =>
									setFormData({
										...formData,
										addressOne: e.target.value,
									})
								}
							/>
						</div>

						<div className='form-group'>
							<label>Address Line 2</label>
							<input
								type='text'
								placeholder='Apt, Suite, etc. (optional)'
								className='form-input'
								value={formData.addressTwo}
								onChange={(e) =>
									setFormData({
										...formData,
										addressTwo: e.target.value,
									})
								}
							/>
						</div>

						<div className='form-row'>
							<div className='form-group'>
								<label>City</label>
								<input
									type='text'
									placeholder='City'
									className='form-input'
									value={formData.city}
									onChange={(e) =>
										setFormData({
											...formData,
											city: e.target.value,
										})
									}
								/>
							</div>
							<div className='form-group'>
								<label>State</label>
								<input
									type='text'
									placeholder='State'
									className='form-input'
									value={formData.state}
									onChange={(e) =>
										setFormData({
											...formData,
											state: e.target.value,
										})
									}
								/>
							</div>
							<div className='form-group'>
								<label>ZIP Code</label>
								<input
									type='text'
									placeholder='ZIP Code'
									className='form-input'
									value={formData.zipCode}
									onChange={(e) =>
										setFormData({
											...formData,
											zipCode: e.target.value,
										})
									}
								/>
							</div>
						</div>

						<div className='form-group'>
							<label>Phone Number</label>
							<input
								type='tel'
								placeholder='Phone number'
								className='form-input'
								value={formData.phoneNumber}
								onChange={(e) =>
									setFormData({
										...formData,
										phoneNumber: e.target.value,
									})
								}
							/>
						</div>

						<div className='form-group'>
							<label>Website</label>
							<input
								type='url'
								placeholder='Website URL'
								className='form-input'
								value={formData.website}
								onChange={(e) =>
									setFormData({
										...formData,
										website: e.target.value,
									})
								}
							/>
						</div>

						<div className='form-group'>
							<label>Facebook</label>
							<input
								type='url'
								placeholder='Facebook URL (optional)'
								className='form-input'
								value={formData.facebook}
								onChange={(e) =>
									setFormData({
										...formData,
										facebook: e.target.value,
									})
								}
							/>
						</div>

						<div className='form-group'>
							<label>X (Twitter)</label>
							<input
								type='url'
								placeholder='X profile URL (optional)'
								className='form-input'
								value={formData.x}
								onChange={(e) =>
									setFormData({
										...formData,
										x: e.target.value,
									})
								}
							/>
						</div>

						<div className='form-group'>
							<label>Instagram</label>
							<input
								type='url'
								placeholder='Instagram URL (optional)'
								className='form-input'
								value={formData.instagram}
								onChange={(e) =>
									setFormData({
										...formData,
										instagram: e.target.value,
									})
								}
							/>
						</div>

						<div className='form-group'>
							<label>App Theme</label>
							<select
								className='form-input'
								value={formData.theme}
								onChange={(e) =>
									setFormData((prev) => ({
										...prev,
										theme: e.target.value,
									}))
								}>
								<option value='default'>Default Theme</option>
								<option value='primary-light'>
									Primary Light Theme
								</option>
							</select>
						</div>

						<div className='color-inputs-row'>
							<div className='form-group'>
								<label>Primary Color</label>
								<div className='color-input-group'>
									<input
										type='color'
										className='form-input color-input'
										value={formData.primaryColor}
										onChange={(e) =>
											handleColorChange(
												'primaryColor',
												e.target.value
											)
										}
									/>
									<input
										type='text'
										className='form-input hex-input'
										value={formData.primaryColor}
										onChange={(e) => {
											const hex =
												e.target.value.startsWith('#')
													? e.target.value
													: `#${e.target.value}`;
											if (/^#[0-9A-Fa-f]{6}$/.test(hex)) {
												handleColorChange(
													'primaryColor',
													hex
												);
											}
										}}
										placeholder='#000000'
									/>
								</div>
							</div>

							<div className='form-group'>
								<label>Secondary Color</label>
								<div className='color-input-group'>
									<input
										type='color'
										className='form-input color-input'
										value={formData.secondaryColor}
										onChange={(e) =>
											handleColorChange(
												'secondaryColor',
												e.target.value
											)
										}
									/>
									<input
										type='text'
										className='form-input hex-input'
										value={formData.secondaryColor}
										onChange={(e) => {
											const hex =
												e.target.value.startsWith('#')
													? e.target.value
													: `#${e.target.value}`;
											if (/^#[0-9A-Fa-f]{6}$/.test(hex)) {
												handleColorChange(
													'secondaryColor',
													hex
												);
											}
										}}
										placeholder='#000000'
									/>
								</div>
							</div>

							<div className='form-group'>
								<label>Accent Color</label>
								<div className='color-input-group'>
									<input
										type='color'
										className='form-input color-input'
										value={formData.accentColor}
										onChange={(e) =>
											handleColorChange(
												'accentColor',
												e.target.value
											)
										}
									/>
									<input
										type='text'
										className='form-input hex-input'
										value={formData.accentColor}
										onChange={(e) => {
											const hex =
												e.target.value.startsWith('#')
													? e.target.value
													: `#${e.target.value}`;
											if (/^#[0-9A-Fa-f]{6}$/.test(hex)) {
												handleColorChange(
													'accentColor',
													hex
												);
											}
										}}
										placeholder='#000000'
									/>
								</div>
							</div>
						</div>

						<div className='submit-container'>
							<button
								type='submit'
								className='submit-btn'>
								Save Changes
							</button>
							{showSuccess && (
								<span className='success-checkmark'>
									<span className='material-icons'>
										check_circle
									</span>
								</span>
							)}
						</div>
					</form>

					<div className='preview-section'>
						<div className='preview-container'>
							<div className='preview-info'>
								<h2>Mobile App Preview</h2>
								<div className='preview-description'>
									<p>
										This is how your organization will
										appear in the mobile app.
									</p>
									<p className='preview-disclaimer'>
										Note: This preview is for visualization
										purposes only and may not exactly
										reflect the final appearance of your
										mobile application.
									</p>
								</div>
							</div>
							<div className='preview-device'>
								<AppPreview orgData={formData} />
							</div>
						</div>

						<div className='qr-code-section'>
							<h2>Organization QR Code</h2>
							<p>
								Scan this QR code to quickly access your
								organization in the mobile app.
							</p>
							<div className='qr-code-container'>
								<OrgQRCode
									orgPin={orgData.pin}
									onQRGenerated={setQrCodeUrl}
								/>
								{qrCodeUrl && (
									<button
										className='download-btn'
										onClick={handleDownloadQR}>
										<span className='material-icons'>
											download
										</span>
										Download QR Code
									</button>
								)}
							</div>
						</div>
					</div>
				</div>
			</div>
		</DashboardLayout>
	);
}

export default DashboardOrganization;
